import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchPracticeCaregivers,
  invalidatePracticeCaregiversCache,
  fetchPracticePatients
} from '../../../store/slices/practiceSlice';
import { deleteCaregiver } from '../../../store/slices/caregiverSlice';
import { toast } from 'react-toastify';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import ReassignmentModal from '../../../components/Common/ReassignmentModal/ReassignmentModal';
import './CaregiverManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';
import { formatDisplayError } from '../../../utils/passwordValidation';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import { appendFromDashboardQuery } from '../../../utils/practiceNavigation';
import { canDeleteCaregiver } from '../../../utils/roleUtils';
import {
  batchReassignPatientsToCaregiver,
  buildEmptyReassignments,
  buildReassignmentsForAll,
  fetchActiveCaregivers,
  fetchPatientsForStaff,
  getCaregiverAssigneeName,
  hasUnassignedReassignments
} from '../../../utils/staffReassignmentFlow';
import { AddCaregiverModal } from './AddCaregiver/AddCaregiverModal';
import { STAFF_LIST_TABLE_CARD, staffCol } from '../PracticeDashboard/staffListTableLayout';
import { matchesWordStartAny } from '../../../utils/searchMatch';

const cgStatus = (s) => {
  const v = (s || 'Active').toString().toLowerCase();
  return v === 'active' ? 'Active' : 'Inactive';
};

const CaregiverManagement = ({ practiceId, isReadOnly = false, onOpenEventLogs = null, hideEventLogsButton = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    caregiversByPracticeId = {},
    practiceCaregiversFetched = {},
    listLoading = {},
    error
  } = useSelector((state) => state.practice ?? {});
  const { user } = useSelector((state) => state.auth);
  const allowDelete = !isReadOnly && canDeleteCaregiver(user);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');

  // Modal state
  const [showAddCaregiverModal, setShowAddCaregiverModal] = useState(false);
  const [caregiverToEdit, setCaregiverToEditModal] = useState(null);
  const [isViewCaregiverModal, setIsViewCaregiverModal] = useState(false);

  const cid = practiceId != null ? String(practiceId) : '';
  const hasCache = cid && practiceCaregiversFetched[cid];
  const practiceCaregivers = cid ? caregiversByPracticeId[cid] ?? [] : [];

  useEffect(() => {
    if (practiceId && !practiceCaregiversFetched[String(practiceId)]) {
      dispatch(fetchPracticeCaregivers(practiceId));
    }
  }, [practiceId, dispatch, practiceCaregiversFetched]);

  // Debounce search term for real-time filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  // Use useMemo for filtering and sorting to prevent unnecessary re-renders and loops
  const filteredCaregivers = useMemo(() => {
    if (!practiceCaregivers || practiceCaregivers.length === 0) return [];

    let filtered = practiceCaregivers.filter(caregiver => {
      const fullName = `${caregiver.first_name || ''} ${caregiver.last_name || ''}`;
      const email = caregiver.email || '';
      const homePhone = caregiver.phone || caregiver.home_phone || '';
      const cellPhone = caregiver.cell_phone || '';

      return matchesWordStartAny([fullName, email, homePhone, cellPhone], searchTerm);
    });

    if (statusFilter !== 'All') {
      filtered = filtered.filter(c => cgStatus(c.status) === statusFilter);
    }

    // Sort caregivers
    return [...filtered].sort((a, b) => {
      if (sortBy === 'idx') {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return sortOrder === 'asc' ? ai - bi : bi - ai;
      }
      if (sortBy === 'status') {
        const statusA = (a.status || 'Active').toLowerCase();
        const statusB = (b.status || 'Active').toLowerCase();

        const statusOrder = { 'active': 1, 'inactive': 2 };
        const orderA = statusOrder[statusA] || 3;
        const orderB = statusOrder[statusB] || 3;

        if (orderA !== orderB) {
          return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
        }
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (sortBy === 'name') {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      } else if (sortBy === 'addr') {
        const valueA = String(a.address_line_1 || a.address?.address_line_1 || '').toLowerCase();
        const valueB = String(b.address_line_1 || b.address?.address_line_1 || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'home') {
        const valueA = String(a.phone || a.home_phone || '').toLowerCase();
        const valueB = String(b.phone || b.home_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'cell') {
        const valueA = String(a.cell_phone || '').toLowerCase();
        const valueB = String(b.cell_phone || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'fax') {
        const valueA = String(a.fax || '').toLowerCase();
        const valueB = String(b.fax || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (sortBy === 'email') {
        const valueA = String(a.email || '').toLowerCase();
        const valueB = String(b.email || '').toLowerCase();
        return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else {
        const valueA = a[sortBy] || '';
        const valueB = b[sortBy] || '';
        return sortOrder === 'asc'
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });
  }, [practiceCaregivers, searchTerm, sortBy, sortOrder, statusFilter]);

  // Reset page only when search or sort parameters change, not on every list update
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  // Calculate pagination
  const entriesPerPageNum = entriesPerPage === 'all' ? filteredCaregivers.length : parseInt(entriesPerPage);
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(filteredCaregivers.length / entriesPerPageNum);
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const endIndex = startIndex + entriesPerPageNum;
  const currentCaregivers = entriesPerPage === 'all' ? filteredCaregivers : filteredCaregivers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };


  const handleViewCaregiver = useCallback((caregiver) => {
    setCaregiverToEditModal(caregiver);
    setIsViewCaregiverModal(true);
    setShowAddCaregiverModal(true);
  }, []);

  const getDisplayName = (caregiver) => {
    const first = caregiver.first_name || '';
    const last = caregiver.last_name || '';
    if (last && first) return `${last}, ${first}`;
    const single = `${first} ${last}`.trim();
    return single || caregiver.name || 'N/A';
  };

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  /** Street lines, then one line: City, State, Zip (state from name or state_id). */
  const formatAddressLines = (caregiver) =>
    buildDetailAddressLines({
      line1: caregiver.address_line_1 ?? caregiver.address?.address_line_1,
      line2: caregiver.address_line_2 ?? caregiver.address?.address_line_2,
      city: caregiver.city ?? caregiver.address?.city,
      stateDisplay: resolveStateDisplay(caregiver),
      zip: caregiver.zip_code ?? caregiver.zipCode ?? caregiver.address?.zip_code
    });


  const getUsername = (caregiver) =>
    caregiver.username || caregiver.user?.username || caregiver.login_username || 'N/A';

  const getTwoWayAuth = (caregiver) => {
    const status =
      caregiver.is_two_way_auth ??
      caregiver.two_way_authentication_enabled ??
      caregiver.twoWayAuth ??
      caregiver.two_way_auth ??
      caregiver.two_way_authentication ??
      caregiver.two_way_authentication_status;

    if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
    if (status === 0 || status === false || status === 'Disabled') return 'Disabled';

    return 'N/A';
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [showDeleteReassignmentModal, setShowDeleteReassignmentModal] = useState(false);
  const [deleteAssignedPatients, setDeleteAssignedPatients] = useState([]);
  const [deleteAlternativeCaregivers, setDeleteAlternativeCaregivers] = useState([]);
  const [deleteReassignments, setDeleteReassignments] = useState({});
  const [isDeleteReassigning, setIsDeleteReassigning] = useState(false);
  const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);

  const handleEditCaregiver = useCallback((caregiverId) => {
    const caregiver = practiceCaregivers.find(c => String(c.id) === String(caregiverId));
    if (caregiver) {
      setCaregiverToEditModal(caregiver);
      setIsViewCaregiverModal(false);
      setShowAddCaregiverModal(true);
    }
  }, [practiceCaregivers]);


  const handleDeleteCaregiver = useCallback((caregiver) => {
    setCaregiverToDelete(caregiver);
    setShowDeleteModal(true);
  }, []);

  const performCaregiverDelete = async (idToDelete, caregiverName) => {
    setShowDeleteModal(false);
    setShowDeleteReassignmentModal(false);
    setCaregiverToDelete(null);
    setDeleteAssignedPatients([]);
    setDeleteAlternativeCaregivers([]);
    setDeleteReassignments({});
    toast.success(`Caregiver ${caregiverName} deleted successfully`);
    try {
      await dispatch(deleteCaregiver({ practiceId, caregiverId: idToDelete })).unwrap();
      dispatch(fetchPracticePatients({ id: practiceId, params: { page: 1, limit: 1000 } }));
    } catch (error) {
      console.error('Error deleting caregiver:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete caregiver';
      toast.error(errorMessage);

      dispatch(invalidatePracticeCaregiversCache(practiceId));
      await dispatch(fetchPracticeCaregivers(practiceId));
    }
  };

  const startCaregiverDeleteFlow = async () => {
    if (!caregiverToDelete) return;

    const idToDelete = caregiverToDelete.id;
    const caregiverName = getDisplayName(caregiverToDelete);
    setShowDeleteModal(false);
    setIsDeleteProcessing(true);

    try {
      const patients = await fetchPatientsForStaff(practiceId, { caregiverId: idToDelete });
      const alternatives = await fetchActiveCaregivers(practiceId, idToDelete);

      setDeleteAssignedPatients(patients);
      setDeleteAlternativeCaregivers(alternatives);

      if (patients.length > 0) {
        setDeleteReassignments(buildEmptyReassignments(patients));
        setShowDeleteReassignmentModal(true);
      } else {
        await performCaregiverDelete(idToDelete, caregiverName);
      }
    } catch (error) {
      console.error('Failed to start caregiver delete flow:', error);
      toast.error('Failed to check assigned patients.');
      setCaregiverToDelete(null);
    } finally {
      setIsDeleteProcessing(false);
    }
  };

  const handleCaregiverReassignAndDelete = async () => {
    if (!caregiverToDelete) return;

    if (hasUnassignedReassignments(deleteAssignedPatients, deleteReassignments)) {
      toast.warning('Please select a new caregiver for all patients.');
      return;
    }

    const idToDelete = caregiverToDelete.id;
    const caregiverName = getDisplayName(caregiverToDelete);
    setIsDeleteReassigning(true);
    try {
      await batchReassignPatientsToCaregiver(practiceId, deleteReassignments);
      await performCaregiverDelete(idToDelete, caregiverName);
    } catch (error) {
      console.error('Caregiver reassignment before delete failed:', error);
      toast.error('Failed to reassign patients.');
    } finally {
      setIsDeleteReassigning(false);
    }
  };

  const cancelCaregiverDeleteReassignment = () => {
    setShowDeleteReassignmentModal(false);
    setCaregiverToDelete(null);
    setDeleteAssignedPatients([]);
    setDeleteAlternativeCaregivers([]);
    setDeleteReassignments({});
  };

  const confirmDelete = startCaregiverDeleteFlow;

  const cancelDelete = () => {
    if (isDeleteProcessing || isDeleteReassigning) return;
    setShowDeleteModal(false);
    setCaregiverToDelete(null);
  };

  const handleAddCaregiver = () => {
    setCaregiverToEditModal(null);
    setIsViewCaregiverModal(false);
    setShowAddCaregiverModal(true);
  };


  const showLoading = practiceId && !hasCache && listLoading.caregivers;

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const columns = useMemo(
    () => [
      staffCol({
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, rowIndex) => <span>{startIndex + rowIndex + 1}</span>
      }, 'idx', true),
      staffCol({
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={cgStatus(row.status)} />
      }, 'status', true),
      staffCol({
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="caregiver-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewCaregiver(row);
            }}
          >
            {getDisplayName(row)}
          </a>
        )
      }, 'name', true),
      staffCol({
        key: 'addr',
        title: 'Address',
        sortable: true,
        render: (row) => {
          const addr = row.address_line_1 || row.address?.address_line_1 || 'N/A';
          return (
            <div
              style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={addr}
            >
              {addr}
            </div>
          );
        }
      }, 'address', true),
      staffCol({ key: 'home', title: 'Home Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.phone || row.home_phone, 'N/A') }, 'homePhone', true),
      staffCol({ key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.cell_phone, 'N/A') }, 'cellPhone', true),
      staffCol({ key: 'fax', title: 'Fax', sortable: true, render: (row) => formatPhoneForDisplay(row.fax, 'N/A') }, 'fax', true),
      staffCol({ key: 'email', title: 'Email', sortable: true, render: (row) => row.email || 'N/A' }, 'email', true),
      staffCol({
        key: 'action',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewCaregiver(row)}
            onEdit={!isReadOnly ? () => handleEditCaregiver(row.id) : null}
            onDelete={allowDelete ? () => handleDeleteCaregiver(row) : null}
          />
        )
      }, 'action', true)
    ],
    [startIndex, isReadOnly, allowDelete, handleEditCaregiver, handleViewCaregiver, handleDeleteCaregiver]
  );

  if (error && !showLoading) {
    return (
      <div className="alert alert-danger" role="alert">
        {formatDisplayError(error)}
      </div>
    );
  }

  return (
    <div className="caregiver-management-container">
      <div className="caregiver-management-header-row">
        <div className="caregiver-management-header-actions">
          <SearchBar
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            className="caregiver-management-search-wrapper"
            placeholder="Search caregivers..."
            label={null}
          />

          <div className="caregiver-management-filter-group">
            <select
              className="caregiver-management-status-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {!isReadOnly && (
            <button className="caregiver-management-add-btn" onClick={handleAddCaregiver}>
              <FontAwesomeIcon icon={faPlus} className="caregiver-management-add-icon" />
              Add Caregiver
            </button>
          )}
        </div>
      </div>

      <div className={STAFF_LIST_TABLE_CARD}>
        <Table
          columns={columns}
          data={currentCaregivers}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          isLoading={showLoading}
          loadingMode="skeleton"
          skeletonRows={10}
          emptyMessage="No caregivers found"
        />

        <ListScreenPaginationBar
          entriesValue={entriesPerPage}
          onEntriesChange={handleEntriesChange}
          currentPage={currentPage}
          totalRecords={filteredCaregivers.length}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          leftSlot={!hideEventLogsButton && (
            <button
              className="caregiver-management-events-log-btn"
              onClick={() => {
                if (typeof onOpenEventLogs === 'function') {
                  onOpenEventLogs();
                  return;
                }
                navigate(appendFromDashboardQuery(`/event-logs/Caregiver/all?practiceId=${practiceId}`, location));
              }}
            >
              <FontAwesomeIcon icon={faList} className="caregiver-management-add-icon" />
              Event Logs
            </button>
          )}
        />
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the caregiver{' '}
                <strong>{getDisplayName(caregiverToDelete)}</strong>?
              </p>
              <p className="modal-warning">
                Assigned patients must be reassigned before this caregiver can be deleted.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={confirmDelete}
                disabled={isDeleteProcessing}
              >
                {isDeleteProcessing ? 'Checking patients...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReassignmentModal
        show={showDeleteReassignmentModal}
        onClose={cancelCaregiverDeleteReassignment}
        title="Reassign Patients"
        description={`Select a new caregiver for each of the ${deleteAssignedPatients.length} patient(s) before deleting ${caregiverToDelete ? getDisplayName(caregiverToDelete) : 'this caregiver'}.`}
        patients={deleteAssignedPatients}
        alternatives={deleteAlternativeCaregivers}
        reassignments={deleteReassignments}
        onReassignmentChange={(patientId, caregiverId) =>
          setDeleteReassignments((prev) => ({ ...prev, [patientId]: caregiverId }))
        }
        onAssignAll={(caregiverId) =>
          setDeleteReassignments(buildReassignmentsForAll(deleteAssignedPatients, caregiverId))
        }
        onSave={handleCaregiverReassignAndDelete}
        isSaving={isDeleteReassigning}
        saveLabel="Reassign & Delete"
        optionPlaceholder="Select caregiver..."
        reassignToLabel="Reassign to caregiver"
        getCurrentAssignee={getCaregiverAssigneeName}
      />

      {/* Add / Edit Caregiver Modal */}
      {showAddCaregiverModal && (
        <AddCaregiverModal
          isOpen={showAddCaregiverModal}
          practiceId={practiceId}
          isViewMode={isViewCaregiverModal}
          isEditMode={!!caregiverToEdit && !isViewCaregiverModal}
          editId={caregiverToEdit?.id}
          onClose={() => {
            const wasView = isViewCaregiverModal;
            setShowAddCaregiverModal(false);
            setCaregiverToEditModal(null);
            setIsViewCaregiverModal(false);
            if (!wasView && practiceId) {
              dispatch(invalidatePracticeCaregiversCache(practiceId));
              dispatch(fetchPracticeCaregivers(practiceId));
            }
          }}
        />
      )}
    </div>
  );
};

export default CaregiverManagement;

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="caregiver-management-detail-row">
    <span className="caregiver-management-detail-label">{label}</span>
    <span className="caregiver-management-detail-separator">:</span>
    <span className={`caregiver-management-detail-value ${multiline ? 'caregiver-management-detail-value--multiline' : ''}`}>
      {Array.isArray(value) ? (
        value.map((line, index) => (
          <Fragment key={`${label}-${index}`}>
            {line || 'N/A'}
            {index < value.length - 1 && <br />}
          </Fragment>
        ))
      ) : (
        value || 'N/A'
      )}
    </span>
  </div>
);